import { TrendingUp } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/lib/currency";
import {
  useExpenseForecast,
  type ForecastCurrency,
} from "@/hooks/finance/cash-flow/useExpenseForecast";

type Props = {
  displayCurrency: ForecastCurrency;
};

/**
 * Upcoming Expense Forecast card — projects each operating cost-group's next 3
 * months from its trailing 3-month average (run-rate). Estimate, not committed.
 */
export function ExpenseForecast({ displayCurrency }: Props) {
  const { data, isLoading } = useExpenseForecast(displayCurrency);
  const fmt = (v: number) => (v ? formatCurrency(v, displayCurrency) : "–");

  const basisLabel = data?.basisMonths.length
    ? `${data.basisMonths[0].label} – ${data.basisMonths[data.basisMonths.length - 1].label}`
    : "";

  return (
    <Card className="border-none shadow-lg overflow-hidden bg-white">
      <CardHeader className="bg-slate-900 border-none pb-4">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-400" />
              Upcoming Expense Forecast
            </CardTitle>
            <CardDescription className="text-slate-400 text-[10px]">
              Operating expenses projected per cost group by extending each
              group's recent trend — an estimate, not a commitment.
            </CardDescription>
          </div>
          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[9px] uppercase font-black tracking-widest">
            Estimate
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-24 bg-slate-50/20">
            <div className="h-9 w-9 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : !data || data.groups.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400">
            Not enough recent expense history to build a forecast.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="sticky left-0 z-10 bg-slate-50 w-64 px-4 py-3 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest border-r border-slate-200">
                    Cost Group
                  </th>
                  <th className="px-4 py-3 text-right text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[110px]">
                    6-mo avg
                  </th>
                  {data.forecastMonths.map((m) => (
                    <th
                      key={m.key}
                      className="px-4 py-3 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest min-w-[110px]"
                    >
                      {m.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest min-w-[120px] border-l border-slate-200">
                    3-mo Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.groups.map((g) => (
                  <tr key={g.label} className="hover:bg-slate-50/50">
                    <td className="sticky left-0 z-10 bg-white px-4 py-2 text-[13px] text-slate-700 border-r border-slate-100">
                      {g.label}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-[12px] text-slate-400">
                      {fmt(g.runRate)}
                    </td>
                    {g.forecast.map((v, i) => (
                      <td
                        key={data.forecastMonths[i].key}
                        className="px-4 py-2 text-right tabular-nums text-[13px] text-slate-700"
                      >
                        {fmt(v)}
                      </td>
                    ))}
                    <td className="px-4 py-2 text-right tabular-nums text-[13px] font-semibold text-slate-900 border-l border-slate-100">
                      {fmt(g.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-900 text-white">
                  <td className="sticky left-0 z-10 bg-slate-900 px-4 py-2.5 text-[13px] font-bold border-r border-slate-700">
                    Total Forecast Expenses
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-[12px] text-slate-400">
                    {fmt(data.grandTotal / (data.forecastMonths.length || 1))}
                  </td>
                  {data.totalByMonth.map((v, i) => (
                    <td
                      key={data.forecastMonths[i].key}
                      className="px-4 py-2.5 text-right tabular-nums text-[13px] font-bold"
                    >
                      {fmt(v)}
                    </td>
                  ))}
                  <td className="px-4 py-2.5 text-right tabular-nums text-[13px] font-black border-l border-slate-700">
                    {fmt(data.grandTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>

            <p className="px-4 py-2.5 text-[10px] text-slate-400 border-t border-slate-100">
              Each month extends the group's linear trend over {basisLabel}; the
              6-mo avg is shown as a baseline. Excludes one-off
              investing/financing outflows. Not a committed budget.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
